import { useAuthStore } from '@/features/auth/store/auth-store';
import { AlertTriangle, XCircle } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { diasQueQuedan } from '@/lib/utils/dias-que-quedan';

/*
  Recorrido 3-nov: la banda era letra ámbar clara sobre fondo beige, casi ilegible. Mismos
  tonos que `Aviso` (components/ui/aviso.tsx): fondo suave del tono, el ícono en el tono y
  el texto en el color normal, en claro y en oscuro.
*/
// Vault 349: en el teléfono el texto ocupa dos líneas y el ícono va con la primera.
const BANDA =
  'flex items-center gap-2 border-b px-4 py-2 text-sm text-foreground max-md:items-start';
const ICONO = 'h-4 w-4 flex-shrink-0 max-md:mt-0.5';
const ALERTA = 'border-alerta-borde bg-alerta-suave';

export function SubscriptionBanner() {
  const { subscription, user } = useAuthStore();

  // No mostrar banner para PLATFORM_ADMIN
  if (user?.role === 'PLATFORM_ADMIN') return null;

  // No mostrar si no hay info de suscripción
  if (!subscription) return null;

  const daysLeft = diasQueQuedan(subscription.endDate) ?? Number.POSITIVE_INFINITY;

  // Expirada. La prueba de una Mini Prixia dice lo suyo y qué hacer (vault 349, P2.6).
  if ((subscription.status === 'EXPIRED' || daysLeft < 0) && subscription.esDemo) {
    return (
      <div role="status" className={cn(BANDA, 'border-bloqueo-borde bg-bloqueo-suave')}>
        <XCircle className={cn(ICONO, 'text-bloqueo')} aria-hidden="true" />
        <span>Tu prueba de Prixia venció. Para seguir, pide el plan completo.</span>
      </div>
    );
  }
  if (subscription.status === 'EXPIRED' || daysLeft < 0) {
    return (
      <div role="status" className={cn(BANDA, 'border-bloqueo-borde bg-bloqueo-suave')}>
        <XCircle className={cn(ICONO, 'text-bloqueo')} aria-hidden="true" />
        <span>Tu suscripción ha expirado. Contacta al administrador para renovar tu plan.</span>
      </div>
    );
  }

  // Próxima a vencer (7 días). La demo vence a los 15 días, como la prueba (2-nov): su propio texto.
  if (daysLeft <= 7 && subscription.esDemo) {
    return (
      <div role="status" className={cn(BANDA, ALERTA)}>
        <AlertTriangle className={cn(ICONO, 'text-alerta')} aria-hidden="true" />
        <span>
          {daysLeft === 0
            ? 'Tu prueba de Prixia vence hoy.'
            : `Tu prueba de Prixia vence en ${daysLeft} ${daysLeft === 1 ? 'día' : 'días'}.`}{' '}
          Para seguir, pide el plan completo.
        </span>
      </div>
    );
  }
  if (daysLeft <= 7) {
    return (
      <div role="status" className={cn(BANDA, ALERTA)}>
        <AlertTriangle className={cn(ICONO, 'text-alerta')} aria-hidden="true" />
        <span>
          {`Tu suscripción (${subscription.planName}) vence en ${daysLeft} ${daysLeft === 1 ? 'día' : 'días'}.`}
        </span>
      </div>
    );
  }

  return null;
}
